import React, { useCallback, useEffect, useMemo, useState } from 'react';

import _ from 'lodash';
import dayjs from 'dayjs';

import { Reorder } from 'framer-motion';

import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { PIPING_ANOMALY, PIPING_CHECKLIST } from '../custom-visual-inspection/piping-items';
import { DEFAULT_REPORT_TITLE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { ChecklistItem, CustomPECSummary, CustomUTMEquipmentSummary, EquipmentAnomalySummaryItem, FVIPipingItem, PDFImageProps, PECScanData, PIDData, User } from '../../../utils/models';
import { getCurrentUser } from '../../../services/authService';
import { getAnomalyStartNumber, getSomeChecklistItemsMissing, getUpdatedEquipmentAnomalySummary, getUpdatedPipingAnomalySummary, matchPhrases, reArrangePhotoRecord, validateWorkOrderNumber } from '../../../utils/utilities';
import { EQUIPMENT_CHECKLIST } from '../custom-visual-inspection/createCustomFVIEquipmentReport';

import AUTGeneralInformationForm from '../../forms/aut/general-information/autGeneralInformationForm';
import AutPhotographicRecordForm from '../../forms/aut/autPhotographicRecord';
import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomAutGeneralDrawingForm from '../../forms/custom-aut/autGeneralDrawing';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomExxonMobilPECReport from '.';
import CustomPECSummaryForm from '../../forms/custom-pec/pecSummaryForm';
import CustomCertificateForm, { ICertificate } from '../custom-general/custom-certificates/form';
import CustomTechnologyIntroductionForm from '../../forms/custom-pec/autTechnologyIntroductionForm';
import CustomPecScanItem from './custom-pec-scan-item';
import CustomNewItem from '../custom-general/custom-new-item';
import EquipmentOverview from '../custom-visual-inspection/equipment-overview';
import EquipmentSummaryForm from '../../forms/custom-fvi/equipmentSummaryForm';
import Elevation from '../../themed/instruction-modals/elevation';
import Modal from '../../themed/modal';
import PECScanDataItemNewForm from '../../forms/pec/scan-data/pecScanDataItemNewForm';
import PECScanPlanForm from '../../forms/pec/scan-and-plan-view/pecScanPlanForm';
import FVIStructurePIDImageForm from '../../forms/custom-fvi/fviStructurePIDImageForm';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import Zippy from '../../themed/zippy';
import ReorderItem from '../../common/reorderItem';
import SyncTime from '../../blocks/sync-time';
import LoadingBadge from '../../themed/loading-badge';
import FVICoverData from '../../themed/fvi-cover-data';
import PipingOverview from '../custom-visual-inspection/piping-overview';
import WarningNotice from '../custom-visual-inspection/warning-notice';

import useAnomalyRecords from '../../../hooks/useAnomalyRecords';
import useAutoSaveReport from '../../../hooks/useAutoSave';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useMappedAnomalies from '../../../hooks/useMappedAnomalies';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

export interface PECFVIData {
	lineNumber: FVIPipingItem['lineNumber'];
	additionalComment: FVIPipingItem['checklistAdditionalComment'];
	anomalySummary: FVIPipingItem['anomalySummary'];
	anomalyItems: FVIPipingItem['anomalyItems'];
	checklistItems: FVIPipingItem['checklistItems'];
	hasAnomalies: FVIPipingItem['hasAnomalies'];
}

const CreateCustomExxonMobilePECReport: React.FC = () => {
	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const { role } = getCurrentUser() as User;
	
	const [certificates, setCertificates] = useState<ICertificate>();
	const [fviData, setFVIData] = useState(initialFVIData);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [previewReport, setPreviewReport] = useState(false);
	const [summaryData, setSummaryData] = useState(initialSummaryData);
	const [technologyIntroduction, setTechnologyIntroduction] = useState('');
	const [generalInfo, setGeneralInfo] = useState(initialGeneralInformation);
	const [pidData, setPIDData] = useState<PIDData>({ hasPID: 'yes', pIDNumber: '', pIDImages: [] });
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [generalDrawing, setGeneralDrawing] = useState<PDFImageProps[]>([]);
	const [photoRecord, setPhotoRecord] = useState<PDFImageProps[]>([]);
	const [pecScans, setPECScans] = useState<PECScanData[]>([]);
	const [scanPlan, setScanPlan] = useState<PDFImageProps[]>([]);

	const handleAddScanItem = useCallback((newScanItem: PECScanData) => {
		const scanMatch = pecScans.find((pecScan) => matchPhrases(pecScan.sectionName, newScanItem.sectionName));
		if (scanMatch) return toastService.success('Scan number already added!');

		setPECScans((previous) => [...previous, newScanItem]);
	}, [pecScans]);

	const handleEditScanItem = useCallback((originalItem: PECScanData, updatedItem: PECScanData) => {
		const updatedItems = pecScans.map((pecScan) => pecScan.sectionName === originalItem.sectionName ? updatedItem : pecScan);
		setPECScans(updatedItems);
	}, [pecScans]);

	const handleDeletePECScanItem = useCallback((itemToDelete: PECScanData) => {
		const items = pecScans.filter((pecScan) => pecScan.sectionName !== itemToDelete.sectionName);
		setPECScans(items);
	}, [pecScans]);

	const reportDetails = useMemo(() => {
		return {
			photoRecord,
			certificates,
			coverData,
			fviData,
			pidData,
			projectDetails,
			generalDrawing,
			generalInfo,
			scanPlan,
			pecScans,
			reportTitle,
			summaryData,
			technologyIntroduction,
		};
	}, [photoRecord, certificates, coverData, fviData, pidData, projectDetails, generalDrawing, generalInfo, scanPlan, summaryData, pecScans, reportTitle, technologyIntroduction]);

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	const mappedAnomalies = useMappedAnomalies([{ id: 1, anomalyItems: fviData.anomalyItems }]);
	
	const checklist = useMemo(() => {
		if (projectDetails) {
			const isNewReport = dayjs(projectDetails.createdAt).isAfter(dayjs('2026-06-08'), 'minutes');
			if (isNewReport) return { pipingChecklist: NEW_PIPING_CHECKLIST, equipmentChecklist: NEW_EQUIPMENT_CHECKLIST };
		}

		return { pipingChecklist: PIPING_CHECKLIST, equipmentChecklist: EQUIPMENT_CHECKLIST };
	}, [projectDetails]);

	const useSwappedScanLabels = useMemo(() => {
		if (!projectDetails) return false;

		return dayjs(projectDetails.createdAt).isAfter(dayjs('2026-08-12'), 'minutes');
	}, [projectDetails]);

	const validationMessage = useMemo(() => {
		if (!reportDetails.summaryData.inspectionType) {
			return 'Provide a valid inspection type in the summary';
		}

		if (!reportDetails.fviData.lineNumber) {
			return 'Provide a valid equipment / line number';
		}

		if (!reportDetails.generalInfo.maxwellEquipmentInformation.footprint) {
			return 'Provide a valid footprint';
		}
		
		if (!reportDetails.generalInfo.maxwellEquipmentInformation.insulationType) {
			return 'Provide an insulation type!';
		}

		if (!reportDetails.generalInfo.maxwellEquipmentInformation.insulationThickness) {
			return 'Provide a valid insulation thickness in mm'
		}

		if (!reportDetails.generalInfo.maxwellEquipmentInformation.dateOfInspection) {
			return 'Please select a valid date of inspection';
		}

		if (!validateWorkOrderNumber(reportDetails.coverData.workOrderNumber)) {
			return 'Enter a valid work order number!';
		}

		const scansWithNoReadings = reportDetails.pecScans.filter((pecScan) => pecScan.readings.data.length === 0);
		
		if (scansWithNoReadings.length > 0) {
			return `Readings Missing for ${scansWithNoReadings.map(scan => scan.sectionName).join(', ')}!`;
		}

		const isReadingCompleted = reportDetails.pecScans.every((scan) => {
			return scan.readings.data.every((reading) => {
				return scan.readings.circValues.every((value) => reading[value]);
			})
		});

		if (!isReadingCompleted) {
			return 'Please ensure that all fields in the scan are completed. Fill in N/A on blank fields!';
		}

		const isChecklistMissing = getSomeChecklistItemsMissing(reportDetails.fviData.checklistItems, mappedAnomalies);
		
		if (isChecklistMissing) {
			return isChecklistMissing;
		}

		if (reportDetails.generalDrawing.length === 0) {
			return 'No Drawing Uploaded!';
		}

		if (reportDetails.pecScans.length === 0) {
			return 'No PEC Scan Data Uploaded!';
		}

		if (reportDetails.scanPlan.length === 0) {
			return 'Please upload scan plan!';
		}
		
		if (reportDetails.photoRecord.length === 0) {
			return 'Please upload photographic record!';
		}

		return '';
	}, [reportDetails, mappedAnomalies]);

	const isFVIIncomplete = useMemo(() => {
		if (!reportDetails.fviData) return true;

		const { anomalySummary, checklistItems, lineNumber } = reportDetails.fviData; 

		const isChecklistMissing = getSomeChecklistItemsMissing(checklistItems, mappedAnomalies);
		const isSummaryIncomplete = Object.keys(anomalySummary).some((key) => _.get(anomalySummary, key) === undefined);

		return isChecklistMissing || isSummaryIncomplete || lineNumber.length === 0;
	}, [reportDetails.fviData, mappedAnomalies]);

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

	const handleUpdateChecklistAndSummary = useCallback((visualInspectionData: PECFVIData, inspectionType: string) => {
		if (!visualInspectionData) {
			return;
		}

		if (_.isEqual(fviData, visualInspectionData)) {
			return;
		}

		if (matchPhrases(inspectionType, 'piping')) {
			const updatedSummary = getUpdatedPipingAnomalySummary(true, visualInspectionData.anomalySummary, visualInspectionData.anomalyItems);
			
			if (!updatedSummary || (fviData.anomalyItems.length > 0 && _.isEqual(updatedSummary, visualInspectionData.anomalySummary))) {
				return;
			}

			const checklistItems = visualInspectionData.checklistItems[0].lineNumber === checklist.pipingChecklist[0].lineNumber ? visualInspectionData.checklistItems : checklist.pipingChecklist;
			return setFVIData({ ...visualInspectionData, checklistItems, anomalySummary: updatedSummary });
		}
		
		const actualAnomalySummary = visualInspectionData.anomalySummary as unknown as EquipmentAnomalySummaryItem;
		const updatedSummary = getUpdatedEquipmentAnomalySummary(true, actualAnomalySummary, visualInspectionData.anomalyItems);
		
		if (!updatedSummary || (fviData.anomalyItems.length > 0 && _.isEqual(updatedSummary, actualAnomalySummary))) {
			return;
		}

		const checklistItems = visualInspectionData.checklistItems[0].lineNumber === checklist.equipmentChecklist[0].lineNumber ? visualInspectionData.checklistItems : checklist.equipmentChecklist;
		setFVIData({ ...visualInspectionData, checklistItems, anomalySummary: updatedSummary as unknown as PECFVIData['anomalySummary'] || actualAnomalySummary });
	}, [fviData, checklist]);

	const handleSummaryDataUpdate = (summaryData: CustomPECSummary) => {
		setSummaryData(summaryData);
		handleUpdateChecklistAndSummary(fviData, summaryData.inspectionType);
	};

	const handleAnomaliesUpdate = (anomalyItems: FVIPipingItem['anomalyItems']) => {
		const updated = { ...fviData, anomalyItems };

    	setFVIData(updated);
    	handleUpdateChecklistAndSummary(updated, summaryData.inspectionType);
	};

	const handleSaveAndPreview = useCallback((): void => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);

	useEffect(() => {
		function initCache() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails;
			
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);

				return;
			}

			if (cachedReport.certificates) {
				setCertificates(cachedReport.certificates);
			}
			
			setCoverData(cachedReport.coverData.coverData.length > 0 ? cachedReport.coverData : fviCoverData);
			setPIDData(cachedReport.pidData);
			setGeneralDrawing(cachedReport.generalDrawing);
			setGeneralInfo(cachedReport.generalInfo);
			setSummaryData(cachedReport.summaryData);
			setScanPlan(cachedReport.scanPlan);
			setPECScans(cachedReport.pecScans);
			setPhotoRecord(reArrangePhotoRecord(cachedReport.photoRecord));
			setReportTitle(cachedReport.reportTitle);
			setTechnologyIntroduction(cachedReport.technologyIntroduction);

			if (cachedReport.fviData) setFVIData(cachedReport.fviData);
			else setFVIData(initialFVIData);
			
			dispatch(setLastPhotoNumber(cachedReport.photoRecord.length));
		}

		initCache();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.PEC));
	}, [dispatch]);

    useAnomalyRecords(fviData?.anomalyItems ?? []);
	useInspectedItemIDUpdate([fviData?.lineNumber]);

	return (
		<>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<CustomExxonMobilPECReport {...reportDetails} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.exxonMobilPEC[projectDetails.organisationId]}
					isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.exxonMobilPEC && role.approveReports}
					subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.inspectionTypeName} Reports`}
				/>
			)}

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice
					title="Information Validation Error!"
					body={validationMessage}
				/>
			</Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>

			<Zippy headerTitle="Executive Summary" isValid={!!summaryData.inspectionType}>
				<CustomPECSummaryForm initialSummaryData={summaryData} onAdd={handleSummaryDataUpdate} />
			</Zippy>

			<CustomCertificateForm calibrationRequired certificates={certificates} onChangeCertificate={setCertificates} />

			<Zippy headerTitle="P & ID" isValid={matchPhrases(pidData.hasPID, 'yes') ? pidData.pIDImages.length > 0 : true}>
				<FVIStructurePIDImageForm initialPIDData={pidData} onAdd={setPIDData} />
			</Zippy>

			<Zippy headerTitle="Drawing" isValid={generalDrawing.length > 0}>
				<CustomAutGeneralDrawingForm initialGeneralDrawing={generalDrawing} onSave={setGeneralDrawing} />
			</Zippy>

			<Zippy headerTitle="General Information" isValid={!!generalInfo.maxwellEquipmentInformation.calibrationDueDate}>
				<div className="p-4 rounded bg-light bordered mb-4">
					<CustomTechnologyIntroductionForm initialTecnologyIntroduction={technologyIntroduction} onSave={setTechnologyIntroduction} />
				</div>
				
				<AUTGeneralInformationForm enableAutData={false} initialData={generalInfo} onProceed={setGeneralInfo} />
			</Zippy>

			{summaryData.inspectionType && config.enableFVIReportEmbedding && (
				<Zippy headerTitle="FVI Anomaly Details" isValid={!isFVIIncomplete}>
					<Conditional isVisible={matchPhrases(summaryData.inspectionType, 'vessel')}>
						<EquipmentSummaryForm
							initialData={{ equipmentNumber: fviData.lineNumber, hasAnomalies: fviData.hasAnomalies }}
							onSave={(data: CustomUTMEquipmentSummary) => setFVIData((previous) => ({ ...previous, ...data, lineNumber: data.equipmentNumber }))}
						/>

						<Conditional isVisible={fviData.lineNumber.length > 0}>
							<div className="p-2 rounded border bg-light mt-4">
								<EquipmentOverview
									equipmentId={1}
									pdoChecklistItems={[]}
									onChangePDOChecklistItems={() => {}}
									anomalyItems={fviData.anomalyItems}
									equipmentNumber={fviData.lineNumber}
									additionalComment={fviData.additionalComment}
									anomalySummary={fviData.anomalySummary as unknown as EquipmentAnomalySummaryItem}
									checklistItems={fviData.checklistItems}
									hasAnomalies={fviData.hasAnomalies}
									nextAnomalyID={getAnomalyStartNumber([fviData])}
									isNewFormat
									isPDOFormat={false}
									isChecklistVisible
									onChangeAnomalies={(anomalyItems) => setFVIData((previous) => ({ ...previous, anomalyItems }))}
									onChangeSummary={(anomalySummary) => setFVIData((previous) => ({ ...previous, anomalySummary: anomalySummary as unknown as PECFVIData['anomalySummary']}))}
									onChangeAdditionalComment={(additionalComment) => setFVIData((previous) => ({ ...previous, additionalComment }))}
									onChangeChecklistItems={(checklistItems) => setFVIData((previous) => ({ ...previous, checklistItems }))}
								/>
							</div>
						</Conditional>
					</Conditional>
					
					<Conditional isVisible={matchPhrases(summaryData.inspectionType, 'piping')}>
						<EquipmentSummaryForm
							initialData={{ equipmentNumber: fviData.lineNumber, hasAnomalies: fviData.hasAnomalies }}
							onSave={(data: CustomUTMEquipmentSummary) => setFVIData((previous) => ({ ...previous, ...data, lineNumber: data.equipmentNumber }))}
						/>

						<Conditional isVisible={fviData.lineNumber.length > 0}>
							<div className="p-2 rounded border bg-light mt-4">
								<PipingOverview
									lineId={1}
									pdoChecklistItems={[]}
									onChangePDOChecklistItems={() => {}}
									isPDOFormat={false}
									anomalyItems={fviData.anomalyItems}
									lineNumber={fviData.lineNumber}
									additionalComment={fviData.additionalComment}
									anomalySummary={fviData.anomalySummary}
									checklistItems={fviData.checklistItems}
									hasAnomalies={fviData.hasAnomalies}
									nextAnomalyID={getAnomalyStartNumber([fviData])}
									isNewFormat
									isChecklistVisible
									onChangeAnomalies={(anomalyItems) => handleAnomaliesUpdate(anomalyItems)}
									onChangeSummary={(anomalySummary) => setFVIData((previous) => ({ ...previous, anomalySummary }))}
									onChangeAdditionalComment={(additionalComment) => setFVIData((previous) => ({ ...previous, additionalComment }))}
									onChangeChecklistItems={(checklistItems) => setFVIData((previous) => ({ ...previous, checklistItems }))}
								/>
							</div>
						</Conditional>
					</Conditional>
				</Zippy>
			)}

			<Zippy headerTitle="Scan Plan" isValid={scanPlan.length > 0}>
				<PECScanPlanForm initialImages={scanPlan} onProceed={setScanPlan} />
			</Zippy>

			<Zippy headerTitle="Scan Data" isValid={pecScans.length > 0}>
				<CustomNewItem label='Add New Scan Section'>
					<PECScanDataItemNewForm onProceed={handleAddScanItem} />
				</CustomNewItem>

				<Reorder.Group className='list-unstyled' values={pecScans} onReorder={setPECScans}>
					{pecScans.map((pecScan, index) => (
						<ReorderItem key={pecScan.sectionName} value={pecScan}>
							<CustomPecScanItem
								onDeletePECScanItem={handleDeletePECScanItem}
								onEditPECScanItem={(updatedItem) => handleEditScanItem(pecScan, updatedItem)}
								isOpenOnDefault={index === (pecScans.length - 1)}
								initialData={pecScan}
								useSwappedLabels={useSwappedScanLabels}
							/>
						</ReorderItem>
					))}
				</Reorder.Group>
			</Zippy>

			<Zippy headerTitle="Photographic Record" isValid={photoRecord.length > 0}>
				<AutPhotographicRecordForm initialImages={photoRecord} onProceed={setPhotoRecord} />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</>
	);
};

const NEW_PIPING_CHECKLIST: ChecklistItem[] = [
    {
        id: '1',
        lineNumber: 'Straight pipe body, Reducer, Tee, connection pipes and reinforcement pads',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '2',
        lineNumber: 'Elbow, Flanges and associated Hardware',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '3',
        lineNumber: 'Support, Support legs and dummy legs',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '4',
        lineNumber: 'Temporary repair clamps and wrapping',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '5',
        lineNumber: 'Pressure and Temperature gauges',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '6',
        lineNumber: 'Safety Valves',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '7',
        lineNumber: 'Coating Condition',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '8',
        lineNumber: 'Uncoated surface, if any',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '9',
        lineNumber: 'Vibration, If any',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '10',
        lineNumber: 'Leak, If any',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },

    // INSULATION
    {
        id: '11',
        lineNumber: 'Gross openings in insulation jacketing',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '12',
        lineNumber: 'Other mechanical damage',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '13',
        lineNumber: 'Loose insulation jacketing',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '14',
        lineNumber: 'Missing insulation jacketing',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '15',
        lineNumber: 'Deterioration of jacketing material',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '16',
        lineNumber: 'Lack of insulation jacketing sealant',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '17',
        lineNumber:
            'Poor sealing at insulation ends and edges particularly at protrusions e.g., small bore attachments',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '18',
        lineNumber: 'Incorrectly installed insulation jacketing',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '19',
        lineNumber: 'Loose screws not giving proper sealing',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '20',
        lineNumber: 'Inadequate overlaps in insulation jacket',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '21',
        lineNumber: 'Bulges in insulation jackets',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '22',
        lineNumber: 'Wet insulation',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '23',
        lineNumber: 'Structures in place to shield item from direct rain',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '24',
        lineNumber: 'Has the insulation thickness been provided as per drawing',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '25',
        lineNumber: 'Surface Temperature',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
];

const NEW_EQUIPMENT_CHECKLIST: ChecklistItem[] = [
    {
        id: '1',
        lineNumber: 'Body or Shell and Transition Cone',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '2',
        lineNumber: 'Head / Dish End',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '3',
        lineNumber: 'Skirt / Support / Support Legs / Flanges / Associated Hardware',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '4',
        lineNumber: 'Small Piping Connections Including Gussets & Safety Valve',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '5',
        lineNumber: 'Platform, Ladder or Other Attachments Welded to Vessel',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '6',
        lineNumber: 'Lifting Lugs / Trunnions',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '7',
        lineNumber: 'Nozzles and Manways Including Reinforcement Pads',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '8',
        lineNumber: 'Coating Condition',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '9',
        lineNumber: 'Level and Temperature Gauges',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '10',
        lineNumber: 'Name Plate',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '11',
        lineNumber: 'Vibration, If Any',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },
    {
        id: '12',
        lineNumber: 'Leak, If Any',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'general',
    },

    // INSULATION
    {
        id: '13',
        lineNumber: 'Gross Openings in Insulation Jacketing',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '14',
        lineNumber: 'Other Mechanical Damage',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '15',
        lineNumber: 'Loose Insulation Jacketing',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '16',
        lineNumber: 'Missing Insulation Jacketing',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '17',
        lineNumber: 'Deterioration of Jacketing Material',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '18',
        lineNumber: 'Lack of Insulation Jacketing Sealant',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '19',
        lineNumber:
            'Poor Sealing at Insulation Ends and Edges Particularly at Protrusions e.g., Small Bore Attachments',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '20',
        lineNumber: 'Incorrectly Installed Insulation Jacketing',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '21',
        lineNumber: 'Loose Screws Not Giving Proper Sealing',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '22',
        lineNumber: 'Inadequate Overlaps in Insulation Jacket',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '23',
        lineNumber: 'Bulges in Insulation Jackets',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '24',
        lineNumber: 'Wet Insulation',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '25',
        lineNumber: 'Structures in Place to Shield Item from Direct Rain',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '26',
        lineNumber: 'Has the Insulation Thickness Been Provided as per Drawing',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
    {
        id: '27',
        lineNumber: 'Surface Temperature',
        assessment: '',
        anomalyItems: [],
        error: true,
        comment: '',
        category: 'insulation',
    },
];

const initialGeneralInformation = {
	systemSettings: {
		prf: '100',
		eht: '300',
		scanMode: 'Bidirectional',
		averagingSample: '2',
		gain: 'variable',
		pulseWidth: 100,
		digitizationRate: 5,
		lowPassFilter: 20,
		highPassFilter: 1.5,
		probeAngle: 0,
		collectionMode: 'Encoded',
		adDelay: 'variable',
		adWidth: 'variable',
	},
	maxwellEquipmentInformation: {
		instrument: 'EDDYFI',
		serialNumber: '1905716',
		cableLength: 5,
		insulationType: '',
		insulationThickness: '',
		footprint: '',
		sizingAlgo: 'CWT applied if found any Isolated Corrosion',
		model: '',
		probeSerialNumber: '',
		dateOfInspection: null,
		calibrationDueDate: null,
	}
};

const initialSummaryData: CustomPECSummary = {
	executiveSummary: '',
	material: '',
	inspectionType: '',
	recommendation: '',
};

const initialFVIData: PECFVIData = {
	lineNumber: '',
	hasAnomalies: 'yes',
	anomalyItems: [],
	anomalySummary: PIPING_ANOMALY,
	additionalComment: '',
	checklistItems: NEW_PIPING_CHECKLIST,
};

export default CreateCustomExxonMobilePECReport;
